import React from "react";
import { X, Sparkles, Key, Check } from "lucide-react";
import redditIcon from "../../assets/images/social/reddit.svg";
import xIcon from "../../assets/images/social/x-twitter.svg";
import linkedinIcon from "../../assets/images/social/linkedin.svg";
import quoraIcon from "../../assets/images/social/quora.svg";
import { useMonitoringSettings } from "../../hooks/use-monitoring-settings";
import "./monitoringSettings.css";
import { MonitoringConfig } from "../../types/add-brand-types";

interface MonitoringSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  monitoringConfig: MonitoringConfig;
  onUpdateKeywordClick: (keywords: string[], platforms: string[]) => void;
}

const MonitoringSettings: React.FC<MonitoringSettingsProps> = ({
  isOpen,
  onClose,
  monitoringConfig,
  onUpdateKeywordClick,
}) => {
  const {
    platforms,
    keywords,
    keywordInput,
    setKeywordInput,
    togglePlatform,
    addKeyword,
    removeKeyword,
    handleKeyPress,
    handleSave,
  } = useMonitoringSettings(monitoringConfig, onUpdateKeywordClick, onClose);

  if (!isOpen) return <></>;

  const getPlatformIcon = (platformId: string) => {
    switch (platformId) {
      case "reddit":
        return redditIcon;
      case "twitter":
        return xIcon;
      case "linkedIn":
        return linkedinIcon;
      case "quora":
        return quoraIcon;
      default:
        return null;
    }
  };

  const getPlatformDisplay = (platformId: string) => {
    switch (platformId) {
      case "twitter":
        return "X (Twitter)";
      case "reddit":
        return "REDDIT";
      case "linkedIn":
        return "LINKEDIN";
      case "quora":
        return "QUORA";
      default:
        return platformId.toUpperCase();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="monitoring-settings-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Monitoring Settings</h2>
            <p className="modal-subtitle">
              Configure your AI agent's listening parameters.
            </p>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {/* Select Sources */}
          <div className="settings-section">
            <div className="section-header">
              <Sparkles size={18} style={{ color: "#4F46E5" }} />
              <h3 className="section-title">Select Sources</h3>
            </div>
            <p className="section-description">
              Choose platforms where the agent should monitor conversations for
              buying intent.
            </p>

            <div className="ms-platforms-grid">
              {platforms.map((platform) => {
                const icon = getPlatformIcon(platform.id);
                return (
                  <button
                    key={platform.id}
                    className={`ms-platform-card ${
                      platform.enabled ? "selected" : ""
                    }`}
                    onClick={() => togglePlatform(platform.id)}
                  >
                    {platform.enabled && (
                      <div className="ms-platform-check">
                        <Check size={16} />
                      </div>
                    )}
                    <div className="ms-platform-icon-wrapper">
                      {icon ? (
                        <img
                          src={icon}
                          alt={platform.name}
                          className="ms-platform-icon"
                        />
                      ) : (
                        <div className="ms-platform-icon-placeholder">
                          {platform.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <span className="ms-platform-name">
                      {getPlatformDisplay(platform.id)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Buying Intent Keywords */}
          <div className="settings-section">
            <div className="section-header">
              <Key size={18} style={{ color: "#4F46E5" }} />
              <h3 className="section-title">Buying Intent Keywords</h3>
            </div>

            <div className="keyword-input-group">
              <input
                type="text"
                className="keyword-input"
                placeholder='Add specific phrases like "looking for alternative to..."'
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <button className="add-keyword-btn" onClick={addKeyword}>
                Add Keyword
              </button>
            </div>

            {keywords.length === 0 ? (
              <div className="keywords-examples">
                <p className="examples-label">Examples:</p>
                <div className="examples-list">
                  <span className="example-text">
                    best alternative to [Competitor]
                  </span>
                  <span className="example-separator">•</span>
                  <span className="example-text">
                    recommendations for [Product]
                  </span>
                  <span className="example-separator">•</span>
                  <span className="example-text">pricing</span>
                </div>
              </div>
            ) : (
              <div className="keywords-list">
                {keywords.map((keyword) => (
                  <div key={keyword} className="keyword-chip">
                    <span className="keyword-text">{keyword}</span>
                    <button
                      className="remove-keyword-btn"
                      onClick={() => removeKeyword(keyword)}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="save-config-btn" onClick={handleSave}>
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default MonitoringSettings;
